import { useState, useEffect } from "react";
import { CreditCard, AlertTriangle, RotateCcw, TrendingDown, Sparkles, Clock, Wallet, PiggyBank, ChevronDown, Calculator, Loader2, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { simulate, formatCurrency, monthsToYears, payoffDate, collectValidDebts } from "@/lib/amortize";
import { DonutChart } from "@/components/DonutChart";
import { BalanceChart } from "@/components/BalanceChart";
import { CardBreakdownTable } from "@/components/CardBreakdownTable";
import { trackWaitlistLead } from "@/lib/analytics";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_DEBTS = 10;
const emptyRow = (_unused, i) => ({ id: `row-${i}`, name: "", balance: "", payment: "", rate: "" });
const initialDebts = () => {
  const rows = Array.from({ length: MAX_DEBTS }, emptyRow);
  rows[0] = { id: "row-0", name: "Credit card 1", balance: "6000", payment: "150", rate: "22.99" };
  return rows;
};

const num = (v) => (v === "" || v === null ? NaN : parseFloat(v));
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const cell = "w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-3 py-2.5 text-base font-medium focus:bg-white focus:ring-2 focus:ring-[#4AAFC9] focus:border-transparent outline-none transition-all tabular-nums";

function DebtRow({ index, debt, onChange, errors }) {
  const upd = (field) => (e) => onChange(index, field, e.target.value);
  const err = (f) => errors[`${index}-${f}`];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[20px_1.7fr_1fr_1fr_1fr] gap-2 sm:gap-3 sm:items-center py-3 sm:py-1.5 border-b border-slate-100 sm:border-0 last:border-0">
      <div className="text-sm font-bold text-slate-400 tabular-nums sm:text-right">{index + 1}.</div>
      <div>
        <label className="sm:hidden text-xs font-bold text-slate-500 uppercase tracking-widest">Debt name</label>
        <input data-testid={`debt-name-${index}`} value={debt.name} onChange={upd("name")} placeholder={`Debt ${index + 1}`} className={cell} />
      </div>
      <div>
        <label className="sm:hidden text-xs font-bold text-slate-500 uppercase tracking-widest">Balance</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">$</span>
          <input data-testid={`debt-balance-${index}`} type="number" min="0" inputMode="decimal" value={debt.balance} onChange={upd("balance")} placeholder="6,000" className={`${cell} pl-7 ${err("balance") ? "border-red-300" : ""}`} />
        </div>
        {err("balance") && <span className="text-xs text-red-600" data-testid={`debt-balance-${index}-error`}>{err("balance")}</span>}
      </div>
      <div>
        <label className="sm:hidden text-xs font-bold text-slate-500 uppercase tracking-widest">Monthly /min payment</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">$</span>
          <input data-testid={`debt-payment-${index}`} type="number" min="0" inputMode="decimal" value={debt.payment} onChange={upd("payment")} placeholder="150" className={`${cell} pl-7 ${err("payment") ? "border-red-300" : ""}`} />
        </div>
        {err("payment") && <span className="text-xs text-red-600" data-testid={`debt-payment-${index}-error`}>{err("payment")}</span>}
      </div>
      <div>
        <label className="sm:hidden text-xs font-bold text-slate-500 uppercase tracking-widest">Interest rate %</label>
        <input data-testid={`debt-rate-${index}`} type="number" min="0" max="100" inputMode="decimal" value={debt.rate} onChange={upd("rate")} placeholder="22.99" className={`${cell} ${err("rate") ? "border-red-300" : ""}`} />
        {err("rate") && <span className="text-xs text-red-600" data-testid={`debt-rate-${index}-error`}>{err("rate")}</span>}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid, sub }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
    <div className="flex items-center gap-2 text-slate-400 mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-bold uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-3xl font-semibold text-[#4AAFC9] tabular-nums tracking-tight" data-testid={testid}>{value}</div>
    {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Radio = ({ name, value, current, onChange, label, testid, tooltip }) => (
  <span className="inline-flex items-center gap-1.5">
    <label className="inline-flex items-center gap-2 cursor-pointer whitespace-nowrap" data-testid={testid}>
      <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} className="w-4 h-4 accent-[#4AAFC9]" />
      <span className="text-base text-slate-700 font-medium">{label}</span>
    </label>
    {tooltip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${value}`}
            data-testid={`${testid}-info`}
            className="text-slate-400 hover:text-[#4AAFC9] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AAFC9] rounded-full"
          >
            <Info className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[260px] bg-slate-900 text-slate-50 text-xs leading-relaxed px-3 py-2"
          data-testid={`${testid}-tooltip`}
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    )}
  </span>
);

export default function DebtCalculator() {
  const [debts, setDebts] = useState(initialDebts);
  const [visible, setVisible] = useState(3);
  const [extraMonthly, setExtraMonthly] = useState("");
  const [extraYearly, setExtraYearly] = useState("");
  const [oneTime, setOneTime] = useState("");
  const [oneTimeMonth, setOneTimeMonth] = useState("5");
  const [strategy, setStrategy] = useState("avalanche");
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");

  // Email gate
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prevent number inputs from changing value when scrolling over them (trackpad/wheel).
  useEffect(() => {
    const onWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number") {
        el.blur();
      }
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  const changeDebt = (i, field, val) => {
    setDebts((prev) => prev.map((d, idx) => (idx === i ? { ...d, [field]: val } : d)));
  };

  const clearAll = () => {
    setDebts(Array.from({ length: MAX_DEBTS }, emptyRow));
    setVisible(3);
    setExtraMonthly(""); setExtraYearly(""); setOneTime(""); setOneTimeMonth("5");
    setStrategy("avalanche");
    setResult(null); setErrors({}); setFormError("");
  };

  const runCalculation = () => {
    const { errors: errs, valid } = collectValidDebts(debts.slice(0, visible));

    setErrors(errs);
    if (Object.keys(errs).length > 0) { setFormError(""); setResult(null); return; }
    if (valid.length === 0) { setFormError("Add at least one debt to calculate your payoff plan."); setResult(null); return; }
    setFormError("");

    const opts = {
      strategy,
      extraMonthly: num(extraMonthly) > 0 ? num(extraMonthly) : 0,
      extraYearly: num(extraYearly) > 0 ? num(extraYearly) : 0,
      oneTime: num(oneTime) > 0 ? num(oneTime) : 0,
      oneTimeMonth: num(oneTimeMonth) > 0 ? Math.round(num(oneTimeMonth)) : 0,
    };
    const plan = simulate(valid, opts);
    const baseline = simulate(valid, { strategy, extraMonthly: 0, extraYearly: 0, oneTime: 0, oneTimeMonth: 0 });
    const hasBoost = opts.extraMonthly || opts.extraYearly || opts.oneTime;

    setResult({ plan, baseline, hasBoost, debtCount: valid.length });
  };

  const handleCalculate = async () => {
    // Email is required right before calculating.
    if (!email || !validateEmail(email)) {
      setEmailError("Please enter a valid email to see your results");
      return;
    }
    setEmailError("");

    // Save email once (deduped server-side). Skip the network call if already saved.
    if (!emailCaptured) {
      setSubmitting(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/waitlist/email`, { email: email.toLowerCase(), source: 'calculator' });
        setEmailCaptured(true);
        if (!res.data?.already_existed) {
          trackWaitlistLead("Calculator Email Capture", "Calculator Email");
        }
      } catch (error) {
        setSubmitting(false);
        toast.error("Couldn't save your email. Please try again.");
        return;
      }
      setSubmitting(false);
    }

    runCalculation();
  };

  const r = result;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-12">
      <header className={`flex flex-col items-start gap-4 mb-10 w-full ${r ? "" : "max-w-2xl mx-auto"}`}>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">Debt payoff calculator</h1>
        <p className="text-base md:text-lg text-slate-500 max-w-xl">Add all your cards and loans, choose a payoff strategy, and see exactly when you'll be debt-free.</p>
      </header>

      <div className={r ? "grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start" : "flex justify-center"}>
        {/* Inputs — single card */}
        <section className={r ? "lg:col-span-7 w-full" : "w-full max-w-2xl"}>
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-6 md:p-8 flex flex-col gap-7">
            {/* Debts */}
            <div>
              <h2 className="text-xl font-medium text-slate-800 mb-5">Your debts</h2>
              <div className="hidden sm:grid grid-cols-[20px_1.7fr_1fr_1fr_1fr] gap-3 px-1 mb-2">
                <span />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Debt name</span>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Balance</span>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Monthly /min payment</span>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Interest %</span>
              </div>
              {debts.slice(0, visible).map((d, i) => (
                <DebtRow key={d.id} index={i} debt={d} onChange={changeDebt} errors={errors} />
              ))}
              {visible < MAX_DEBTS && (
                <button data-testid="show-more-button" onClick={() => setVisible((v) => Math.min(v + 3, MAX_DEBTS))} className="mt-3 text-[#4AAFC9] hover:text-[#357f93] font-semibold text-sm inline-flex items-center gap-1.5 transition-colors">
                  <ChevronDown className="w-4 h-4" /> Show more input fields
                </button>
              )}
            </div>

            {/* Extra payments */}
            <div className="border-t border-slate-100 pt-6 flex flex-col gap-3">
              <h2 className="text-xl font-medium text-slate-800 mb-1">Extra payments</h2>
              <div className="flex flex-nowrap items-center gap-3">
                <div className="relative w-24 shrink-0">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">$</span>
                  <input data-testid="extra-monthly" type="number" min="0" value={extraMonthly} onChange={(e) => setExtraMonthly(e.target.value)} placeholder="0" className={`${cell} pl-7`} />
                </div>
                <span className="text-slate-600 font-medium whitespace-nowrap">per month</span>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <div className="relative w-24 shrink-0">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">$</span>
                  <input data-testid="extra-yearly" type="number" min="0" value={extraYearly} onChange={(e) => setExtraYearly(e.target.value)} placeholder="0" className={`${cell} pl-7`} />
                </div>
                <span className="text-slate-600 font-medium whitespace-nowrap">per year</span>
              </div>
            </div>

            {/* Strategy */}
            <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <h2 className="text-xl font-medium text-slate-800 whitespace-nowrap">Payoff strategy</h2>
              <div className="flex flex-wrap gap-x-6 gap-y-3 min-w-0">
                <TooltipProvider delayDuration={150}>
                  <Radio name="strategy" value="avalanche" current={strategy} onChange={setStrategy} testid="strategy-avalanche"
                    label={<><span>Avalanche</span><span className="text-slate-400 hidden md:inline"> · highest APR</span></>}
                    tooltip="Pay minimums on all debts, then use the extra cash for the highest-interest balance first. Saves the most money over time." />
                  <Radio name="strategy" value="snowball" current={strategy} onChange={setStrategy} testid="strategy-snowball"
                    label={<><span>Snowball</span><span className="text-slate-400 hidden md:inline"> · lowest balance</span></>}
                    tooltip="Pay minimums on all debts, then pay the smallest balance first. Slower mathematically and you pay more in interest, but the quick wins build motivation." />
                </TooltipProvider>
              </div>
            </div>

            {/* Email gate + Actions */}
            <div className="border-t border-slate-100 pt-6 flex flex-col gap-5">
              <div className="flex flex-col gap-1.5 max-w-md">
                <label htmlFor="cal-email" className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  Your email <span className="text-[#4AAFC9]">*</span>
                </label>
                <input
                  id="cal-email"
                  data-testid="calculator-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className={`${cell} ${emailError ? "border-red-300" : ""}`}
                />
                {emailError && <span className="text-xs text-red-600" data-testid="calculator-email-error">{emailError}</span>}
                <span className="text-xs text-slate-400">We'll add you on the waitlist for Resfi early access. Don't worry, we won't spam you.</span>
              </div>

              <div className="flex items-center gap-4">
                <button data-testid="calculate-button" onClick={handleCalculate} disabled={submitting} className="bg-gradient-to-r from-[#4AAFC9] to-[#85B7EB] text-white font-semibold rounded-2xl px-8 py-3.5 inline-flex items-center gap-2 shadow-lg shadow-[#4AAFC9]/25 hover:shadow-xl hover:shadow-[#4AAFC9]/30 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0">
                  {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Calculating…</>) : (<><Calculator className="w-5 h-5" /> Calculate</>)}
                </button>
                <button data-testid="clear-button" onClick={clearAll} className="text-slate-500 hover:text-slate-800 font-medium transition-colors py-2 px-4 rounded-xl hover:bg-slate-100 inline-flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" /> Clear
                </button>
              </div>
              {formError && <p className="text-sm font-medium text-red-600 -mt-2" data-testid="form-error">{formError}</p>}
            </div>
          </div>
        </section>

        {/* Results */}
        {r && (
        <section className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-24">
          {r && r.plan.neverPayoff && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-6 shadow-sm flex flex-col gap-3" data-testid="warning-never-payoff">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-7 h-7 text-red-600 shrink-0" />
                <h2 className="text-xl font-semibold">These debts will never be fully paid off</h2>
              </div>
              <p className="text-base leading-relaxed">
                Your payments of <strong className="tabular-nums">{formatCurrency(r.plan.monthlyCommit)}</strong>/month don't cover the combined monthly interest of{" "}
                <strong className="tabular-nums" data-testid="combined-interest">{formatCurrency(r.plan.firstInterest)}</strong>. You'd need to pay at least{" "}
                <strong className="tabular-nums" data-testid="min-to-progress">{formatCurrency(r.plan.minToProgress)}</strong>/month to start making progress.
              </p>
            </div>
          )}

          {r && !r.plan.neverPayoff && (
            <>
              <div className="grid grid-cols-1 gap-4">
                <Stat icon={Clock} label="Time to debt-free" value={`${r.plan.months} mo`} sub={`${monthsToYears(r.plan.months)} · debt-free by ${payoffDate(r.plan.months)}`} testid="result-months" />
                <div className="grid grid-cols-2 gap-4">
                  <Stat icon={Wallet} label="Total paid" value={formatCurrency(r.plan.totalPaid, 0)} testid="result-total-paid" />
                  <Stat icon={TrendingDown} label="Total interest" value={formatCurrency(r.plan.totalInterest, 0)} testid="result-total-interest" />
                </div>
              </div>

              {r.hasBoost && (r.baseline.neverPayoff || r.baseline.months > r.plan.months) && (
                <div className="bg-gradient-to-br from-[#F0F9FB] to-white border border-[#4AAFC9]/20 rounded-2xl p-6 shadow-sm" data-testid="savings-callout">
                  <div className="flex items-center gap-2 mb-3 text-[#4AAFC9]">
                    <Sparkles className="w-5 h-5" />
                    <span className="text-xs font-bold uppercase tracking-widest">Your plan vs minimums only</span>
                  </div>
                  {r.baseline.neverPayoff ? (
                    <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug">
                      Paying only the minimums, these debts would <span className="text-red-600">never be paid off</span>. Your plan clears them in{" "}
                      <span className="text-[#4AAFC9]">{monthsToYears(r.plan.months)}</span>.
                    </p>
                  ) : (
                    <>
                      <p className="text-2xl md:text-3xl font-semibold text-slate-900 leading-snug">
                        You save <span className="text-[#4AAFC9]" data-testid="months-saved">{r.baseline.months - r.plan.months} months</span> and{" "}
                        <span className="text-[#4AAFC9]" data-testid="interest-saved">{formatCurrency(r.baseline.totalInterest - r.plan.totalInterest, 0)}</span> in interest
                      </p>
                      <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-[#4AAFC9]/15">
                        <div>
                          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Minimums only</div>
                          <div className="text-lg font-semibold text-slate-700 tabular-nums">{payoffDate(r.baseline.months)}</div>
                          <div className="text-sm text-slate-400">{monthsToYears(r.baseline.months)}</div>
                        </div>
                        <div>
                          <div className="text-xs font-bold text-[#4AAFC9] uppercase tracking-widest mb-1">Your plan</div>
                          <div className="text-lg font-semibold text-[#4AAFC9] tabular-nums" data-testid="new-payoff-date">{payoffDate(r.plan.months)}</div>
                          <div className="text-sm text-slate-400">{monthsToYears(r.plan.months)}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-6 md:p-8">
                <div className="flex items-center gap-2 text-slate-400 mb-5">
                  <PiggyBank className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-widest">Principal vs interest</span>
                </div>
                <DonutChart principal={r.plan.principal} interest={r.plan.totalInterest} />
              </div>

              <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-6 md:p-8">
                <BalanceChart schedule={r.plan.schedule} />
                <p className="text-xs text-slate-400 mt-4">Combined balance across {r.debtCount} {r.debtCount === 1 ? "debt" : "debts"} over time.</p>
              </div>
            </>
          )}
        </section>
        )}
      </div>

      {r && !r.plan.neverPayoff && r.plan.cards && r.plan.cards.length > 1 && (
        <div className="mt-6 lg:mt-10">
          <CardBreakdownTable cards={r.plan.cards} />
        </div>
      )}

      <footer className="text-center text-sm text-slate-400 mt-12">Estimates only · APR compounded monthly (APR ÷ 12) · Calculated entirely in your browser · resfi</footer>
    </div>
  );
}
